/**
 * Pure pricing math for the Tour Dates Automation Package: tiers priced by the
 * cost-plus formula in docs/specs/credits-pricing-approach.md, then
 * charm-rounded. Tier identity and allotments come from ./automations-plans.
 */

import {
  CREDITS_PER_TEXT,
  OVERAGE_CENTS_PER_10K_CREDITS,
} from "./automations-overage";
import {
  AUTOMATIONS_TIER_CREDITS,
  AUTOMATIONS_TIERS,
  isAutomationsTier,
  type AutomationsTier,
} from "./automations-plans";

export const PLATFORM_FEE_DOLLARS = 20;
export const MIN_MONTHLY_PRICE_DOLLARS = 35;
export const ANNUAL_DISCOUNT = 0.2;

export type TierId = AutomationsTier;

export interface Tier {
  id: TierId;
  name: string;
  creditAllotment: number;
  monthlyDollars: number;
  annualDollars: number;
  annualPerMonthDollars: number;
}

const NICE_TOLERANCE_DOLLARS = 0.005;

/**
 * "Charm pricing" rounder: snaps a raw dollar figure to the nearest of
 * {decade-1 (prev …9), decade+5, decade+9} — e.g. 76.3 → 75, 77.5 → 79,
 * 80 → 79, 100 → 99. A value within `NICE_TOLERANCE_DOLLARS` of the START of
 * the NEXT decade (e.g. 49.9975) rounds UP into that decade directly.
 */
export function roundNice(value: number): number {
  if (value <= 0) return 0;

  const decadeStart = Math.floor(value / 10) * 10;
  if (value - decadeStart >= 10 - NICE_TOLERANCE_DOLLARS) {
    return decadeStart + 10;
  }

  const candidates = [decadeStart - 1, decadeStart + 5, decadeStart + 9];
  let nearest = candidates[0];
  for (const candidate of candidates.slice(1)) {
    if (Math.abs(value - candidate) < Math.abs(value - nearest)) {
      nearest = candidate;
    }
  }
  return Math.max(0, nearest);
}

/** Multiply first, divide once — `credits * cents` is exact, so the single
 *  division stays exactly rounded through the charm-rounding tolerance. */
function rawDollarsForCredits(credits: number): number {
  return (
    PLATFORM_FEE_DOLLARS +
    (Math.max(0, credits) * OVERAGE_CENTS_PER_10K_CREDITS) / 1_000_000
  );
}

export function tierPriceDollars(creditAllotment: number): {
  monthly: number;
  annual: number;
  annualPerMonth: number;
} {
  const monthly = Math.max(
    MIN_MONTHLY_PRICE_DOLLARS,
    roundNice(rawDollarsForCredits(creditAllotment))
  );
  const annual = roundNice(monthly * 12 * (1 - ANNUAL_DISCOUNT));
  return { monthly, annual, annualPerMonth: Math.round(annual / 12) };
}

export const TIER_NAMES: Record<TierId, string> = {
  starter: "Starter",
  pro: "Pro",
  feature: "Feature",
  headliner: "Headliner",
};

/** Display name for a raw Stripe `metadata.tier`; null when unset or not a known tier. */
export function automationsTierName(
  tier: string | null | undefined
): string | null {
  return isAutomationsTier(tier) ? TIER_NAMES[tier] : null;
}

/** Ascending by allotment/price, in `AUTOMATIONS_TIERS` order. */
export const TIERS: readonly Tier[] = AUTOMATIONS_TIERS.map((id) => {
  const creditAllotment = AUTOMATIONS_TIER_CREDITS[id];
  const { monthly, annual, annualPerMonth } = tierPriceDollars(creditAllotment);
  return {
    id,
    name: TIER_NAMES[id],
    creditAllotment,
    monthlyDollars: monthly,
    annualDollars: annual,
    annualPerMonthDollars: annualPerMonth,
  };
});

/** 1 - annual / (monthly x 12), unrounded, from the charm-rounded prices. */
export function annualSavingsPct(tier: Tier): number {
  return 1 - tier.annualDollars / (tier.monthlyDollars * 12);
}

export function getTier(id: TierId): Tier {
  const tier = TIERS.find((t) => t.id === id);
  if (!tier) throw new Error(`Unknown automations tier: ${id}`);
  return tier;
}

/** The extra credits (and their text equivalent) a monthly overage cap buys before billing stops. */
export function overageCapSummary(capCents: number): {
  credits: number;
  texts: number;
} {
  const credits =
    capCents > 0
      ? Math.floor((capCents * 10_000) / OVERAGE_CENTS_PER_10K_CREDITS)
      : 0;
  return { credits, texts: Math.floor(credits / CREDITS_PER_TEXT) };
}
